import React, { useEffect, useRef, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { useSidebar } from "./SidebarContext";
import { HandCoins } from "lucide-react";
import { getAdminPermission, SubAdminPermission } from "../CommonAPI/Admin";

const Sidebar = ({ permissionData }) => {
  const role = localStorage.getItem("Role");
  const name = localStorage.getItem("name");
  const { activeItem, setActiveItem } = useSidebar();

  const [isActive, setIsActive] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [permission, setPermission] = useState(
    JSON.parse(localStorage.getItem("SubAdminPermission")) || []
  );

  const userPermission = localStorage.getItem("Permission") || "";
  const expire = localStorage.getItem("expire");
  const sidebarRef = useRef(null);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
    document.body.classList.toggle("sidebar-open"); // body par class toggle
  };
  // ✅ Set favicon & title once
  useEffect(() => {
    const panelName = localStorage.getItem("pannel_name");
    const faviconUrl = localStorage.getItem("fevicon");

    if (panelName) document.title = panelName;

    if (faviconUrl) {
      let favicon =
        document.querySelector("link[rel='icon']") ||
        document.createElement("link");
      favicon.rel = "icon";
      favicon.type = "image/x-icon";
      favicon.href = faviconUrl;
      document.head.appendChild(favicon);
    }
  }, []);

  // ✅ Fetch permissions (Admin + Subadmin)
  useEffect(() => {
    getAdminPermission()
      .then((response) => {
        if (response.Status) {
          localStorage.setItem(
            "adminPermission",
            JSON.stringify(response.Data)
          );
        }
      })
      .catch((err) => console.error("Error fetching admin permissions", err));

    const fetchSubAdmin = async () => {
      const req = { username: name };
      await SubAdminPermission(req)
        .then((response) => {
          if (response.Status) {
            localStorage.setItem(
              "SubAdminPermission",
              JSON.stringify(response.Data)
            );
            setPermission(response.Data);
          }
        })
        .catch((err) =>
          console.log("Error in fetching SubAdmin permission", err)
        );
    };
    fetchSubAdmin();
  }, [name]);

  // ✅ Sidebar expand/collapse
  useEffect(() => {
    const sidebar = sidebarRef.current;

    const handleSidebarItemClick = (event) => {
      const li = event.currentTarget;
      const submenu = li.querySelector(".iq-submenu");

      if (submenu) {
        submenu.style.display = li.classList.toggle("menu-open")
          ? "block"
          : "none";
      }
    };

    const sidebarItems = sidebar?.querySelectorAll(".iq-sidebar-menu li") || [];
    sidebarItems.forEach((item) =>
      item.addEventListener("click", handleSidebarItemClick)
    );

    return () => {
      sidebarItems.forEach((item) =>
        item.removeEventListener("click", handleSidebarItemClick)
      );
    };
  }, []);

  // ✅ Handle sidebar click
  const handleSidebarClick = (e, item) => {
    const hasChild = e.currentTarget.querySelector(".iq-submenu");
    setActiveItem(item);

    if (!hasChild && document.body.classList.contains("sidebar-open")) {
      document.body.classList.remove("sidebar-open");
      setIsSidebarOpen(false);
    }
  };

  // ✅ Define menu items
  const sidebarItems = useMemo(() => {
    const baseItems = {
      Admin: [
        {
          path: "/admin/dashboard",
          icon: <i className="ri-home-8-line" />,
          label: "Dashboard",
        },
        {
          path: "/admin/clientservice",
          icon: <i className="ri-group-line" />,
          label: "Clients",
        },
        {
          path: "/admin/allSubadmin",
          icon: <i className="fa fa-user-check"></i>,
          label: "Subadmins",
        },
        {
          path: "/admin/allplan",
          icon: <i className="ri-file-list-3-line" />,
          label: "Plans",
        },
        {
          path: "/admin/tradehistory",
          icon: <i className="ri-history-line"></i>,
          label: <>Trade History</>,
        },
        {
          path: "/admin/strategygroup",
          icon: <i className="fa fa-chess"></i>,
          label: <>Strategy Bot</>,
        },
        {
          path: "/admin/trade-response",
          icon: <i className="fa fa-exchange-alt"></i>,
          label: <>Trade Response</>,
        },
      ],
      Superadmin: [
        {
          path: "/superadmin/dashboard",
          icon: <i className="ri-home-fill" />,
          label: "Dashboard",
        },
        {
          path: "/superadmin/create-admin",
          icon: <i className="fa fa-user-plus" />,
          label: "Create Admin",
        },
        {
          path: "/superadmin/admin-details",
          icon: <i className="fa fa-id-card" />,
          label: "Admin Details",
        },
        {
          path: "/superadmin/amount-details",
          icon: <i className="fa fa-dollar-sign" />,
          label: "Amount Details",
        },
        {
          path: "/superadmin/client-thread-report",
          icon: <i className="ri-bar-chart-fill" />,
          label: <>Client Thread Report</>,
        },
        {
          path: "/superadmin/client-trade-response",
          icon: <i className="fa fa-reply"></i>,
          label: <>Client Trade Response</>,
        },
        {
          path: "/superadmin/update-client-details",
          icon: <i className="fa fa-user-edit"></i>,
          label: <>Update Client Details</>,
        },
        {
          path: "/superadmin/api-create-info",
          icon: <i className="fa fa-code"></i>,
          label: "ApiCreateInfo",
        },
        {
          path: "/superadmin/New-Update",
          icon: <i className="fa fa-bell"></i>,
          label: "New-Update",
        },
        {
          path: "/superadmin/admin-offer",
          icon: <i className="fa fa-hand-holding-usd"></i>,
          label: <>Admin Details</>,
        },
      ],
      Subadmin: [
        {
          path: "/subadmin/dashboard",
          icon: <i className="ri-dashboard-fill" />,
          label: "Dashboard",
        },
        {
          path: "/subadmin/allclient",
          icon: <i className="fa fa-users" />,
          label: "Clients",
          permission: ["AddClient", "ViewClient", "EditClient"],
        },
        {
          path: "/subadmin/groups",
          icon: <i className="fa fa-users-cog" />,
          label: "Strategy Bot",
        },
        {
          path: "/subadmin/trade-history",
          icon: <i className="fa fa-history"></i>,
          label: "Trade History",
          permission: ["TradeHistory"],
        },
        {
          path: "/subadmin/trade-response",
          icon: <i className="fa fa-exchange-alt"></i>,
          label: "Trade Response",
        },
        {
          path: "/subadmin/transaction-history",
          icon: <i className="fa fa-receipt"></i>,
          label: "Transaction History",
        },
        {
          path: "/subadmin/AllPlan",
          icon: <i className="fa fa-receipt"></i>,
          label: "All Plan",
        },
      ],
      User: [
        {
          path: "/user/dashboard",
          icon: <i className="ri-home-8-line" />,
          label: "Dashboard",
        },
        {
          path: "tradereport",
          icon: <i className="fa fa-chart-line" />,
          label: "Trade Report",
        },
        {
          path: "tradehistory",
          icon: <i className="fa fa-history"></i>,
          label: "Trade History",
        },
        {
          path: "traderesponse",
          icon: <i className="fa fa-random" />,
          label: "Trade Response",
        },
        {
          path: "profitandloss",
          icon: <i className="fa fa-balance-scale" />,
          label: "Net P&L",
        },
        {
          path: "StrategyTag",
          icon: <i className="ri-lightbulb-flash-fill" />,
          label: "Strategy Tag",
        },
        {
          path: "technical/pattern",
          icon: <i className="fa fa-cogs" />,
          label: "Tech Pattern",
        },
        {
          path: "lastpattern",
          icon: <i className="fa fa-cog" />,
          label: "Last Pattern",
        },
        {
          path: "all/plan",
          icon: <i className="ri-folder-chart-line" />,
          label: "Plans",
        },
        {
          path: "pannel",
          icon: <i className="fa fa-th" />,
          label: "Panel Track",
        },
        {
          path: "discription",
          icon: <i className="fa fa-align-left" />,
          label: "Description",
        },
      ],
    };

    // ✅ Extra permission-based items
    if (
      permissionData?.includes("ChartingPlatform") ||
      permissionData?.includes("OverallPlan")
    ) {
      baseItems.Admin.push({
        path: "/admin/Strategy-tag",
        icon: <i className="fa fa-chess-knight"></i>,
        label: <>Strategy Tag</>,
      });
    }

    if (permissionData?.includes("Option Chain")) {
      baseItems.Admin.push({
        path: "/admin/OptionChain",
        icon: <i className="ri-stock-line" />,
        label: <>Option Chain</>,
      });

      if (userPermission?.includes("Option Chain")) {
        baseItems.User.push({
          path: "/user/OptionChain",
          icon: <i className="ri-stock-line" />,
          label: <>Option Chain</>,
        });
      }
    }

    return baseItems;
  }, [permissionData, userPermission]);

  // ✅ Render filtered menu
  const getSidebarMenu = () => {
    const safePermission = permission || [];
    return (
      sidebarItems[role]?.filter(
        (item) =>
          !item.permission ||
          item.permission.some((p) => safePermission.includes(p))
      ) || []
    );
  };

  return (
    <>
      <div className={`iq-sidebar sidebar-left ${isActive ? "open" : ""}`}>
        <div className="iq-sidebar-logo d-flex justify-content-between align-items-center">
          <img
            className="header_img2 header-logo-img"
            alt="Logo"
            id="header_img2"
            src={localStorage.getItem("logo")}
          />
          <button className=" menusidebar" onClick={toggleSidebar}>
            <i className="fa-solid fa-angle-right fs-5"></i>
          </button>
        </div>
        <hr />
        <div
          id="sidebar-scrollbar"
          data-scrollbar="true"
          tabIndex={-1}
          className="mt-0"
          // style={{ overflow: "hidden", outline: "none" }}
        >
          <div className="scroll-content">
            <nav ref={sidebarRef} className="iq-sidebar-menu">
              <ul className="iq-menu">
                {getSidebarMenu().map((item) => (
                  <li
                    key={item.path}
                    className={activeItem === item.path ? "active" : ""}
                    onClick={(e) => handleSidebarClick(e, item.path)}
                  >
                   <Link
  to={String(expire || "").includes("1") ? "/user/all/plan" : item.path}
  className="iq-waves-effect sidebar-left"
>
  <OverlayTrigger
    placement="right"
    delay={{ show: 250, hide: 400 }}
    overlay={<Tooltip id={`tooltip-${item.path}`}>{item.label}</Tooltip>}
  >
    <div className="d-inline-block link-icon">{item.icon}</div>
  </OverlayTrigger>
  <span className="ml-4">{item.label}</span>
</Link>

                  </li>
                ))}
              </ul>
            </nav>
            <div className="pb-2" />
          </div>
        </div>
      </div>
      {isActive && (
        <div className="sidebar-overlay" onClick={() => setIsActive(false)} />
      )}
    </>
  );
};

export default Sidebar;
